import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { DollarSign, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function StudentFeesPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');

  useEffect(() => {
    fetchFeeData();
  }, []);

  const fetchFeeData = async () => {
    try {
      const paymentsRes = await api.get('/fee-payments');
      setPayments(paymentsRes.data);
    } catch (error) {
      toast.error('Failed to fetch fee data');
    } finally {
      setLoading(false);
    }
  };

  const quarters = [
    { id: 'Q1', name: 'Quarter 1', months: 'Apr - Jun' },
    { id: 'Q2', name: 'Quarter 2', months: 'Jul - Sep' },
    { id: 'Q3', name: 'Quarter 3', months: 'Oct - Dec' },
    { id: 'Q4', name: 'Quarter 4', months: 'Jan - Mar' }
  ];

  const getQuarterStatus = (quarterId) => {
    const payment = payments.find(p => p.quarter === quarterId);
    if (!payment) return 'pending';
    return payment.status;
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    toast.info('Payment integration coming soon! For now, contact school admin.');
    setShowPaymentModal(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-outfit font-semibold text-2xl text-slate-800">Fee Payment</h2>
        <p className="text-slate-500">View and pay your school fees</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {quarters.map(quarter => {
          const status = getQuarterStatus(quarter.id);
          const payment = payments.find(p => p.quarter === quarter.id);
          const borderClass = status === 'paid' ? 'border-green-200' : status === 'partial' ? 'border-yellow-200' : 'border-slate-100';
          
          return (
            <div key={quarter.id} className={`bg-white rounded-2xl border shadow-sm p-6 ${borderClass}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-outfit font-semibold text-lg text-slate-800">{quarter.name}</h3>
                  <p className="text-sm text-slate-500">{quarter.months}</p>
                </div>
                {status === 'paid' ? <CheckCircle className="w-6 h-6 text-green-600" /> : <AlertCircle className="w-6 h-6 text-orange-600" />}
              </div>
              
              {payment ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Amount</span>
                    <span className="font-medium text-slate-800">₹{payment.amount.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Status</span>
                    <span className={status === 'paid' ? 'px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700' : status === 'partial' ? 'px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700' : 'px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700'}>
                      {status}
                    </span>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setSelectedQuarter(quarter.id); setShowPaymentModal(true); }} className="w-full mt-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-2 text-sm font-medium transition-colors">
                  Pay Now
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <h3 className="font-outfit font-semibold text-lg text-slate-800 mb-4">Payment History</h3>
        {payments.length > 0 ? (
          <div className="space-y-3">
            {payments.map(payment => (
              <div key={payment.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div>
                  <p className="font-medium text-slate-800">{payment.quarter} - {quarters.find(q => q.id === payment.quarter)?.months}</p>
                  <p className="text-sm text-slate-500">{new Date(payment.paymentDate).toLocaleDateString()}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-slate-800">₹{payment.amount.toLocaleString()}</p>
                  <span className={payment.status === 'paid' ? 'px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700' : payment.status === 'partial' ? 'px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700' : 'px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700'}>
                    {payment.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-slate-500 py-8">No payment history</p>
        )}
      </div>

      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowPaymentModal(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-outfit font-semibold text-xl text-slate-800 mb-4">Pay Fee - {selectedQuarter}</h3>
            <form onSubmit={handlePayment} className="space-y-4">
              <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                <p className="text-sm text-indigo-700 mb-1">Amount to Pay</p>
                <p className="text-2xl font-outfit font-bold text-indigo-600">₹10,000</p>
                <p className="text-xs text-indigo-600 mt-1">Tuition + Transport + Other charges</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Payment Method</label>
                <select className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" required>
                  <option value="">Select Payment Method</option>
                  <option value="upi">UPI</option>
                  <option value="card">Credit/Debit Card</option>
                  <option value="netbanking">Net Banking</option>
                </select>
              </div>
              
              <div className="flex gap-3">
                <button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-2.5 font-medium">
                  Proceed to Payment
                </button>
                <button type="button" onClick={() => setShowPaymentModal(false)} className="flex-1 border border-slate-200 hover:bg-slate-50 rounded-full py-2.5 font-medium">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
